import fs from "fs";
import path from "path";
import os from "os";
import { findPackageJson, safeReadJson } from "../utils/files.js";
import { detectProjectType } from "../project/projectDetector.js";
import { resolvePackageManager } from "../packageManager/packageManagerService.js";
import { getNodeVersion } from "../utils/exec.js";

export interface DiagnosticBundle {
  timestamp: string;
  command: string;
  projectType: string;
  reactNativeVersion: string;
  expoVersion?: string;
  packageManager: string;
  nodeVersion: string;
  os: {
    platform: string;
    arch: string;
  };
  error?: {
    message: string;
    stack?: string;
  };
}

/**
 * Generates a diagnostic support bundle inside .kyro/reports/ on failure.
 */
export async function generateDiagnosticBundle(
  command: string,
  error?: Error
): Promise<string> {
  const cwd = process.cwd();
  const packageJsonPath = findPackageJson(cwd);
  const projectRoot = packageJsonPath ? path.dirname(packageJsonPath) : cwd;

  // 1. Gather React Native and Expo version details
  let rnVersion = "unknown";
  let expoVersion: string | undefined = undefined;

  if (packageJsonPath) {
    const pkg = safeReadJson(packageJsonPath);
    if (pkg) {
      const dependencies = (pkg["dependencies"] as Record<string, string>) || {};
      const devDependencies = (pkg["devDependencies"] as Record<string, string>) || {};
      const allDeps = { ...dependencies, ...devDependencies };
      
      const rnSpec = allDeps["react-native"];
      if (rnSpec) {
        rnVersion = rnSpec.replace(/^[\^~>=<]+/, "");
      }
      const expoSpec = allDeps["expo"];
      if (expoSpec) {
        expoVersion = expoSpec.replace(/^[\^~>=<]+/, "");
      }
    }
  }

  // 2. Gather environment metadata
  const projectType = detectProjectType(cwd);
  let pmName = "unknown";
  try {
    const pmResolution = await resolvePackageManager(cwd);
    pmName = pmResolution.selected;
  } catch {
    // Ignore PM resolution errors
  }

  const nodeVersion = (await getNodeVersion()) || process.version;
  const platform = os.platform();
  const arch = os.arch();

  // 3. Build diagnostic bundle payload
  const bundle: DiagnosticBundle = {
    timestamp: new Date().toISOString(),
    command,
    projectType,
    reactNativeVersion: rnVersion,
    expoVersion,
    packageManager: pmName,
    nodeVersion,
    os: {
      platform,
      arch,
    },
  };

  if (error) {
    bundle.error = {
      message: error.message || "Unknown error",
      stack: error.stack,
    };
  }

  // 4. Save to target report path
  const reportsDir = path.join(projectRoot, ".kyro", "reports");
  if (!fs.existsSync(reportsDir)) {
    fs.mkdirSync(reportsDir, { recursive: true });
  }

  const safeTimestamp = new Date().toISOString().replace(/:/g, "-");
  const reportFilename = `report-${safeTimestamp}.json`;
  const reportPath = path.join(reportsDir, reportFilename);

  fs.writeFileSync(reportPath, JSON.stringify(bundle, null, 2), "utf-8");

  // Return the relative path of the file
  return path.relative(cwd, reportPath);
}
